/**
 * EN | ES switcher.
 *
 * Switching keeps you where you are. The locale lives in the path, so the
 * toggle rewrites only the prefix and carries the rest — pathname, search
 * and hash — across: /es/about#visit becomes /about#visit, never "/".
 *
 * A plain <a>, not a router <Link>: a client-side push puts the hash in the
 * URL without re-triggering the browser's jump to it, so switching language
 * from /#menu left you at the top of /es reading the right words in the
 * wrong place. A full navigation gets native hash behaviour and starts the
 * app cleanly on one dictionary. Changing language is a deliberate,
 * once-a-visit action; a reload is the honest cost.
 *
 * The choice is remembered in localStorage but NEVER acted on automatically.
 * No redirect on load, from storage or from navigator.language: a visitor
 * who lands on an English link should get English, and a shared /es link
 * must open in Spanish for whoever opens it. Storage is a record of what
 * this person picked, for anything that later wants to ask.
 */

import { useLocation } from 'react-router-dom';
import { useCopy, useLocale, type Locale } from '../../content';
import { useShopConfig } from '../../hooks/useShopConfig';

export const LOCALE_STORAGE_KEY = 'kd_locale';

export function rememberLocale(locale: Locale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    /* private mode / storage disabled — the toggle still works, it just forgets */
  }
}

/** Same page, other language. Keeps search and hash. */
export function pathForLocale(pathname: string, target: Locale): string {
  const bare = pathname.replace(/^\/es(?=\/|$)/, '') || '/';
  return target === 'es' ? `/es${bare === '/' ? '' : bare}` || '/es' : bare;
}

export default function LocaleToggle({ className = '' }: { className?: string }) {
  const copy = useCopy();
  const { t } = useShopConfig();
  const active = useLocale();
  const { pathname, search, hash } = useLocation();

  const option = (locale: Locale, label: string) => {
    const on = locale === active;
    return (
      <a
        key={locale}
        href={`${pathForLocale(pathname, locale)}${search}${hash}`}
        onClick={() => rememberLocale(locale)}
        aria-current={on ? 'true' : undefined}
        lang={locale}
        className={`rounded-full px-2 py-1 font-oswald text-[11px] font-medium uppercase tracking-[0.12em] transition-colors ${
          on ? 'bg-navy text-paper' : 'text-navy/55 hover:text-navy'
        }`}
      >
        {label}
      </a>
    );
  };

  return (
    <nav
      aria-label={t(copy.localeToggle.label)}
      className={`flex shrink-0 items-center gap-0.5 rounded-full border border-navy/20 p-0.5 ${className}`}
    >
      {option('en', t(copy.localeToggle.en))}
      {option('es', t(copy.localeToggle.es))}
    </nav>
  );
}
